/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { examApi } from '../services/api';
import { Exam, Question } from '../types';

// Ensure KaTeX CSS is present so math from QuestionEditor renders correctly in preview/print
const ensureKatexCss = () => {
    if (document.getElementById('katex-css')) return;
    const link = document.createElement('link');
    link.id = 'katex-css';
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.10/dist/katex.min.css';
    document.head.appendChild(link);
};

const ExamPreview: React.FC = () => {
    const { examId } = useParams<{ examId: string }>();
    const [searchParams] = useSearchParams();
    const [exam, setExam] = useState<Exam | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const wantAutoDownload = useMemo(() => searchParams.get('download') === '1', [searchParams]);
    const realExamMode = useMemo(() => (searchParams.get('mode') || '').toLowerCase() === 'exam', [searchParams]);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await examApi.getById(Number(examId));
                setExam(data);
            } catch (e) {
                console.error('Failed to load exam', e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [examId]);

    useEffect(() => {
        ensureKatexCss();
    }, []);

    useEffect(() => {
        if (!loading && exam && wantAutoDownload) {
            // Delay slightly to allow layout
            setTimeout(() => {
                window.print();
            }, 250);
        }
    }, [loading, exam, wantAutoDownload]);

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!exam) {
        return (
            <div className="empty-state">
                <div className="empty-state-emoji">😕</div>
                <h3 className="empty-state-title">Exam Not Found</h3>
            </div>
        );
    }

    const renderQuestion = (q: Question, index: number) => {
        const number = index + 1;
        const hasPrimaryImage = !!q.imageAsPrimary && !!q.imagePath;
        return (
            <div key={q.id ?? index} className="card" style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <span className="question-number">{number}.</span>
                            <span style={{ color: '#718096' }}>
                                ({q.marks} mark{(q.marks || 0) > 1 ? 's' : ''})
                            </span>
                        </div>
                        <div style={{ marginTop: 6 }}>
                            {hasPrimaryImage ? (
                                <img src={q.imagePath} alt={`Q${number}`} style={{ maxWidth: '100%', maxHeight: 320 }} />
                            ) : q.questionRichText ? (
                                <div dangerouslySetInnerHTML={{ __html: q.questionRichText }} />
                            ) : (
                                <div>{q.questionText}</div>
                            )}
                        </div>

                        {/* Reference image (if not primary) */}
                        {!hasPrimaryImage && q.imagePath && (
                            <div style={{ marginTop: '0.5rem' }}>
                                <img src={q.imagePath} alt={`Reference for Q${number}`} style={{ maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                            </div>
                        )}
                        {/* Shapes preview (if any) */}
                        {q.shapesJson && q.shapesJson !== '[]' && (
                            <div style={{ marginTop: 8 }}>
                                <small style={{ color: '#718096' }}>(Geometric diagram not interactive in preview)</small>
                            </div>
                        )}

                        {/* Answers area (read-only). Do not reveal correctness. */}
                        {q.questionType === 'MCQ' ? (
                            <div style={{ marginTop: 10 }}>
                                {(q.answers || []).map((a, i) => (
                                    <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <input type="radio" name={`q_${q.id ?? index}`} disabled />
                                        <span>{a.answerText}</span>
                                    </label>
                                ))}
                            </div>
                        ) : (
                            <div style={{ marginTop: 10 }}>
                                <div style={{ minHeight: 90, border: '1px dashed #cbd5e0', padding: 8, borderRadius: 8 }}>
                                    <em style={{ color: '#718096' }}>Write your answer here...</em>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const totalMarks = (exam.questions || []).reduce((sum, q) => sum + (q.marks || 0), 0);

    return (
        <div data-testid="exam-preview">
            {/* Print styles */}
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .card { box-shadow: none !important; border: 1px solid #ddd !important; }
                    .app .main-content { padding: 0 !important; }
                }
                /* Preserve colors and backgrounds when printing */
                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                }
            `}</style>

            {!realExamMode && (
                <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <h1 style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>👁️ Exam Preview</h1>
                    <p style={{ opacity: 0.9, margin: 0 }}>Read-only view (no submission)</p>
                </div>
            )}

            {/* Header with actions */}
            {!realExamMode && (
                <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0.75rem 0', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-outline" onClick={() => navigate(-1)}>← Back</button>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary" onClick={() => window.print()} title="Download as PDF using your browser's print to PDF">
                            ⬇️ Download / Print
                        </button>
                    </div>
                </div>
            )}

            {/* Exam metadata */}
            <div className="card">
                <h2 style={{ margin: 0 }}>{exam.title}</h2>
                {exam.description && (
                    <p style={{ marginTop: 6 }}>{exam.description}</p>
                )}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', color: '#4a5568' }}>
                    <div>⏱ Duration: {exam.durationMinutes ?? '-'} mins</div>
                    <div>📊 Total Marks: {exam.totalMarks ?? totalMarks}</div>
                    {exam.categoryName && <div>📘 Subject: {exam.categoryName}</div>}
                    {exam.classLevel && <div>🎓 Class: {exam.classLevel}</div>}
                </div>
            </div>

            {/* Questions */}
            <div style={{ marginTop: '1rem' }}>
                {(exam.questions || []).length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-emoji">📝</div>
                        <h3 className="empty-state-title">No Questions</h3>
                        <p>This exam has no questions yet.</p>
                    </div>
                ) : (
                    <div>
                        {(exam.questions || []).map((q, i) => renderQuestion(q, i))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamPreview;
